import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type CurrentUserService from './current-user';

export interface Command {
  id: string;
  title: string;
  description?: string;
  icon?: string;
  category?: string;
  shortcut?: string;
  action: () => void | Promise<void>;
  isAvailable?: () => boolean;
}

export interface CommandGroup {
  category: string;
  commands: Command[];
}

export default class CommandPaletteService extends Service {
  @service declare router: RouterService;
  @service declare currentUser: CurrentUserService;

  @tracked recentCommands: string[] = [];
  @tracked isExecuting = false;

  private commands: Command[] = [];
  private readonly RECENT_COMMANDS_KEY = 'command-palette-recent';
  private readonly MAX_RECENT_COMMANDS = 10;

  constructor(owner: any) {
    super(owner);
    this.initializeCommands();
    this.loadRecentCommands();
  }

  private initializeCommands() {
    this.commands = [
      // Navigation Commands
      {
        id: 'nav-home',
        title: 'Go to Home',
        description: 'Navigate to the homepage',
        icon: 'home',
        category: 'Navigation',
        action: () => this.router.transitionTo('index'),
      },
      {
        id: 'nav-esports',
        title: 'Go to Esports',
        description: 'View esports coverage and tournaments',
        icon: 'trophy',
        category: 'Navigation',
        action: () => this.router.transitionTo('esports'),
      },
      {
        id: 'nav-news',
        title: 'Go to News',
        description: 'View latest gaming news',
        icon: 'news',
        category: 'Navigation',
        action: () => this.router.transitionTo('news'),
      },
      {
        id: 'nav-search',
        title: 'Search',
        description: 'Open search functionality',
        icon: 'search',
        category: 'Navigation',
        shortcut: 'Ctrl+/',
        action: () => {
          // This would trigger the search overlay
          document.dispatchEvent(new CustomEvent('open-search'));
        },
      },

      // User Commands
      {
        id: 'user-profile',
        title: 'View Profile',
        description: 'Go to your user profile',
        icon: 'user',
        category: 'User',
        action: () => {
          if (this.currentUser.user) {
            this.router.transitionTo('user.profile', this.currentUser.user.id);
          }
        },
        isAvailable: () => !!this.currentUser.user,
      },
      {
        id: 'user-settings',
        title: 'Settings',
        description: 'Open user settings',
        icon: 'settings',
        category: 'User',
        action: () => this.router.transitionTo('settings'),
        isAvailable: () => !!this.currentUser.user,
      },
      {
        id: 'user-logout',
        title: 'Log Out',
        description: 'Sign out of your account',
        icon: 'logout',
        category: 'User',
        action: async () => {
          if (
            this.currentUser &&
            typeof this.currentUser.logout === 'function'
          ) {
            await this.currentUser.logout();
          }
          this.router.transitionTo('index');
        },
        isAvailable: () => !!this.currentUser.user,
      },
      {
        id: 'user-login',
        title: 'Log In',
        description: 'Sign in to your account',
        icon: 'login',
        category: 'User',
        action: () => this.router.transitionTo('login'),
        isAvailable: () => !this.currentUser.user,
      },

      // Quick Actions
      {
        id: 'action-copy-url',
        title: 'Copy Current URL',
        description: 'Copy the current page URL to clipboard',
        icon: 'link',
        category: 'Actions',
        action: async () => {
          try {
            await navigator.clipboard.writeText(window.location.href);
            // Show success feedback (could integrate with a toast service)
            console.log('URL copied to clipboard');
          } catch (error) {
            console.warn('Failed to copy URL:', error);
          }
        },
      },
      {
        id: 'action-refresh',
        title: 'Refresh Page',
        description: 'Reload the current page',
        icon: 'refresh',
        category: 'Actions',
        shortcut: 'Ctrl+R',
        action: () => window.location.reload(),
      },

      // Theme/Appearance
      {
        id: 'theme-toggle',
        title: 'Toggle Theme',
        description: 'Switch between light and dark mode',
        icon: 'moon',
        category: 'Appearance',
        action: () => {
          // This would integrate with a theme service
          document.documentElement.classList.toggle('dark-theme');
        },
      },

      // Help & Support
      {
        id: 'help-keyboard',
        title: 'Keyboard Shortcuts',
        description: 'View available keyboard shortcuts',
        icon: 'keyboard',
        category: 'Help',
        action: () => {
          // This would open a help modal or navigate to help page
          console.log('Show keyboard shortcuts');
        },
      },
      {
        id: 'help-support',
        title: 'Contact Support',
        description: 'Get help and support',
        icon: 'help',
        category: 'Help',
        action: () => this.router.transitionTo('support'),
      },
    ];
  }

  getAvailableCommands(): Command[] {
    return this.commands.filter((command) => {
      return command.isAvailable ? command.isAvailable() : true;
    });
  }

  getCommandsByCategory(): CommandGroup[] {
    const availableCommands = this.getAvailableCommands();
    const grouped = availableCommands.reduce(
      (groups: { [key: string]: Command[] }, command) => {
        const category = command.category || 'Other';
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category]!.push(command);
        return groups;
      },
      {},
    );

    return Object.entries(grouped).map(([category, commands]) => ({
      category,
      commands,
    }));
  }

  searchCommands(query: string): Command[] {
    if (!query.trim()) {
      return this.getAvailableCommands();
    }

    const normalizedQuery = query.toLowerCase().trim();
    const availableCommands = this.getAvailableCommands();

    return availableCommands
      .filter((command) => {
        const searchableText = [
          command.title,
          command.description,
          command.category,
          command.shortcut,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchableText.includes(normalizedQuery);
      })
      .sort((a, b) => {
        // Prioritize title matches over description matches
        const aTitle = a.title.toLowerCase().includes(normalizedQuery);
        const bTitle = b.title.toLowerCase().includes(normalizedQuery);

        if (aTitle && !bTitle) return -1;
        if (!aTitle && bTitle) return 1;

        return 0;
      });
  }

  getRecentCommands(): Command[] {
    return this.recentCommands
      .map((commandId) => this.commands.find((cmd) => cmd.id === commandId))
      .filter((command): command is Command => Boolean(command))
      .filter((command) => (command.isAvailable ? command.isAvailable() : true))
      .slice(0, 5); // Show only top 5 recent commands
  }

  getDefaultActions(): Command[] {
    // Return default navigation actions: Home, eSports, News
    const defaultIds = ['nav-home', 'nav-esports', 'nav-news'];
    return this.commands
      .filter((cmd) => defaultIds.includes(cmd.id))
      .filter((command) => (command.isAvailable ? command.isAvailable() : true));
  }

  async executeCommand(command: Command): Promise<void> {
    if (this.isExecuting) return;

    this.isExecuting = true;

    try {
      await command.action();
      this.addToRecentCommands(command.id);
    } catch (error) {
      console.error('Failed to execute command:', command.id, error);
    } finally {
      this.isExecuting = false;
    }
  }

  private addToRecentCommands(commandId: string) {
    // Remove if already exists
    this.recentCommands = this.recentCommands.filter((id) => id !== commandId);

    // Add to beginning
    this.recentCommands.unshift(commandId);

    // Limit size
    this.recentCommands = this.recentCommands.slice(
      0,
      this.MAX_RECENT_COMMANDS,
    );

    // Persist to localStorage
    this.saveRecentCommands();
  }

  clearRecentCommands() {
    this.recentCommands = [];
    this.saveRecentCommands();
  }

  private loadRecentCommands() {
    try {
      const stored = localStorage.getItem(this.RECENT_COMMANDS_KEY);
      if (stored) {
        this.recentCommands = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load recent commands:', error);
      this.recentCommands = [];
    }
  }

  private saveRecentCommands() {
    try {
      localStorage.setItem(
        this.RECENT_COMMANDS_KEY,
        JSON.stringify(this.recentCommands),
      );
    } catch (error) {
      console.warn('Failed to save recent commands:', error);
    }
  }
}
